import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../apiConfig";
import { readAloudBrowserFallbackAllowed } from "../utils/readAloudPolicy";
import { logProductEvent } from "../utils/productAnalytics";
import {
  ensureVoicesReady,
  getSavedTtsPrefs,
  listVoices,
  normalizeTtsText,
  pickVoice,
} from "../tts";

export function useReadAloud(lang) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const activeNormRef = useRef("");
  const serverAudioRef = useRef(null);
  const utterRef = useRef(null);

  const stop = useCallback(() => {
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* ignore */
    }
    if (serverAudioRef.current) {
      try {
        serverAudioRef.current.pause();
      } catch {
        /* ignore */
      }
      serverAudioRef.current = null;
    }
    activeNormRef.current = "";
    setIsSpeaking(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const playServer = useCallback(
    async (normalized) => {
      if (!normalized) return false;
      try {
        setIsSpeaking(true);
        activeNormRef.current = normalized;
        if (serverAudioRef.current) {
          try {
            serverAudioRef.current.pause();
          } catch {
            /* ignore */
          }
          serverAudioRef.current = null;
        }
        const res = await fetch(`${API_BASE}/api/ai/speak`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: normalized, lang }),
        });
        if (!res.ok) {
          stop();
          return false;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        serverAudioRef.current = audio;
        audio.onended = () => {
          URL.revokeObjectURL(url);
          stop();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          stop();
        };
        try {
          await audio.play();
          logProductEvent("tts_listen", { source: "server" }, lang);
          return true;
        } catch {
          URL.revokeObjectURL(url);
          stop();
          return false;
        }
      } catch {
        stop();
        return false;
      }
    },
    [lang, stop]
  );

  const speakWithBrowser = useCallback(
    (normalized) => {
      if (!window.speechSynthesis) return;
      activeNormRef.current = normalized;
      const synth = window.speechSynthesis;
      try {
        synth.cancel();
        if (synth.paused) synth.resume();
      } catch {
        /* ignore */
      }

      const utter = new SpeechSynthesisUtterance(normalized);
      utter.lang = lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "he-IL";
      const { rate, pitch } = getSavedTtsPrefs(lang);
      if (rate) utter.rate = rate;
      if (pitch) utter.pitch = pitch;
      const voice = pickVoice(lang);
      if (voice) {
        utter.voice = voice;
        if (voice.lang) utter.lang = voice.lang;
      }
      utter.onstart = () => {
        activeNormRef.current = normalized;
        setIsSpeaking(true);
        logProductEvent("tts_listen", { source: "browser" }, lang);
      };
      utter.onend = () => {
        activeNormRef.current = "";
        setIsSpeaking(false);
      };
      utter.onerror = () => {
        activeNormRef.current = "";
        setIsSpeaking(false);
      };
      utterRef.current = utter;
      synth.speak(utter);
      if (!listVoices().length) {
        ensureVoicesReady().then(() => {
          if (!synth.speaking && utterRef.current === utter) {
            synth.speak(utter);
          }
        });
      }
    },
    [lang]
  );

  const speak = useCallback(
    (rawText) => {
      const normalized = normalizeTtsText(rawText);
      if (!normalized) return;
      stop();
      void (async () => {
        const ok = await playServer(normalized);
        if (ok) return;
        if (readAloudBrowserFallbackAllowed() && window.speechSynthesis) {
          speakWithBrowser(normalized);
        }
      })();
    },
    [playServer, speakWithBrowser, stop]
  );

  const toggle = useCallback(
    (rawText) => {
      const normalized = normalizeTtsText(rawText);
      if (!normalized) return;
      if (isSpeaking && activeNormRef.current === normalized) {
        stop();
        return;
      }
      speak(rawText);
    },
    [isSpeaking, speak, stop]
  );

  return { isSpeaking, speak, stop, toggle };
}
